import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "react-hot-toast";
import { API_ENDPOINTS } from "../../../constants/config";

export function ScholarshipForm({ editingScholarship, onSuccess }) {
  const [scholarshipName, setScholarshipName] = useState("");
  const [scholarshipAmount, setScholarshipAmount] = useState("");
  const [scholarshipType, setScholarshipType] = useState("Rs");
  const [scholarshipFrequency, setScholarshipFrequency] = useState("Monthly");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getSchoolId = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const s = user?.school;
    if (!s) return null;
    return typeof s === "object" && s._id != null ? s._id : s;
  };

  useEffect(() => {
    if (editingScholarship) {
      setScholarshipName(editingScholarship.name);
      setScholarshipAmount(editingScholarship.amount.toString());
      setScholarshipType(editingScholarship.type);
      setScholarshipFrequency(editingScholarship.frequency);
    } else {
      setScholarshipName("");
      setScholarshipAmount("");
      setScholarshipType("Rs");
      setScholarshipFrequency("Monthly");
    }
  }, [editingScholarship]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!scholarshipName || !scholarshipAmount) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    const schoolId = getSchoolId();
    const scholarshipData = {
      name: scholarshipName,
      amount: parseFloat(scholarshipAmount),
      type: scholarshipType,
      frequency: scholarshipFrequency,
      schoolId
    };

    try {
      if (editingScholarship) {
        const response = await fetch(
          API_ENDPOINTS.scholarship(editingScholarship._id),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            credentials: "include",
            body: JSON.stringify(scholarshipData),
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Failed to update scholarship");
        }

        toast.success("Scholarship updated successfully");
      } else {
        const response = await fetch(API_ENDPOINTS.scholarships, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          credentials: "include",
          body: JSON.stringify(scholarshipData),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Failed to create scholarship");
        }

        toast.success("Scholarship created successfully");
      }

      setScholarshipName("");
      setScholarshipAmount("");
      setScholarshipType("Rs");
      setScholarshipFrequency("Monthly");

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error(error.message || "Failed to save scholarship");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setScholarshipName("");
    setScholarshipAmount("");
    setScholarshipType("Rs");
    setScholarshipFrequency("Monthly");
    if (onSuccess) {
      onSuccess();
    }
  };

  return (
    <div className="border rounded-xl p-6 mt-8 bg-[#f7faff] shadow-sm mx-auto">
      <h5 className="font-semibold text-lg text-[#0263cc] mb-6 flex items-center gap-2">
        <span className="inline-block w-1 h-5 bg-[#0263cc] rounded-full"></span>
        {editingScholarship ? "Edit Scholarship" : "Add New Scholarship"}
      </h5>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="scholarshipName" className="mb-1 block text-[#0263cc]">
              Name of Scholarship <span className="text-red-500">*</span>
            </Label>
            <Input
              id="scholarshipName"
              className="bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0263cc]"
              placeholder="Scholarship name"
              value={scholarshipName}
              onChange={(e) => setScholarshipName(e.target.value)}
              disabled={isSubmitting}
              autoComplete="off"
            />
          </div>

          <div>
            <Label className="mb-1 block text-[#0263cc]">Type & Amount <span className="text-red-500">*</span></Label>
            <div className="flex gap-3 items-center mb-2">
              <RadioGroup
                value={scholarshipType}
                onValueChange={setScholarshipType}
                className="flex gap-4"
                disabled={isSubmitting}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="%" id="percentage" />
                  <Label htmlFor="percentage">%</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Rs" id="rupees" />
                  <Label htmlFor="rupees">Rs</Label>
                </div>
              </RadioGroup>
            </div>
            <Input
              type="number"
              min="0"
              className="bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0263cc]"
              placeholder="Amount"
              value={scholarshipAmount}
              onChange={(e) => setScholarshipAmount(e.target.value)}
              disabled={isSubmitting}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="frequency" className="mb-1 block text-[#0263cc]">Frequency</Label>
            <Select
              value={scholarshipFrequency}
              onValueChange={setScholarshipFrequency}
              disabled={isSubmitting}
            >
              <SelectTrigger id="frequency" className="bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0263cc]">
                <SelectValue placeholder="Frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Monthly">Monthly</SelectItem>
                <SelectItem value="Annual">Annual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="deduction" className="mb-1 block text-[#0263cc]">Deduct from fees</Label>
            <Select defaultValue="Monthly" disabled>
              <SelectTrigger id="deduction" className="bg-gray-100 border border-gray-300 rounded-lg">
                <SelectValue placeholder="Monthly" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-4 mt-4">
          <Button
            type="submit"
            className="bg-[#0263cc] hover:bg-[#024b99] text-white font-semibold rounded-lg px-6 py-2 shadow transition"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Saving..."
              : editingScholarship
                ? "Update"
                : "+ Add"}
          </Button>
          {editingScholarship && (
            <Button
              type="button"
              variant="outline"
              className="border border-[#0263cc] text-[#0263cc] hover:bg-[#0263cc]/10 rounded-lg px-6 py-2 transition"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
